import { useMemo } from 'react';
import type { FieldSlot } from '../utils/passEditState';

export const usePassFieldHighlight = (
  fieldSlots: FieldSlot[],
  focusedField?: string | null
) => {
  const fieldIdToHighlightId = useMemo(() => {
    const map: Record<string, string> = {};
    fieldSlots.forEach(([section, slot, fieldDef]) => {
      if (fieldDef?.id) map[fieldDef.id] = `${section}.${slot}`;
    });
    return map;
  }, [fieldSlots]);

  const highlightIdToFieldId = useMemo(() => {
    const map: Record<string, string> = {};
    Object.entries(fieldIdToHighlightId).forEach(([fieldId, highlightId]) => {
      map[highlightId] = fieldId;
    });
    return map;
  }, [fieldIdToHighlightId]);

  const { focusedFieldId, focusedLocale } = useMemo(() => {
    if (!focusedField) return { focusedFieldId: null, focusedLocale: null };
    const lastDot = focusedField.lastIndexOf('.');
    if (lastDot === -1) return { focusedFieldId: null, focusedLocale: null };
    const prefix = focusedField.substring(0, lastDot);
    const suffix = focusedField.substring(lastDot + 1);
    const fieldId = highlightIdToFieldId[prefix];
    if (!fieldId) return { focusedFieldId: null, focusedLocale: null };
    const isProperty = ['defaultValue', 'label', 'id'].includes(suffix);
    return {
      focusedFieldId: fieldId,
      focusedLocale: isProperty ? null : suffix,
    };
  }, [focusedField, highlightIdToFieldId]);

  const handleFieldSectionFocus = (
    section: 'front' | 'back',
    onActiveSection?: (section: 'front' | 'back', locale?: string | null) => void,
    onFieldFocus?: (id: string | null) => void
  ) =>
    (fieldId: string, locale?: string | null) => {
      onActiveSection?.(section, locale);
      const highlightId = fieldIdToHighlightId[fieldId];
      if (!highlightId) { onFieldFocus?.(null); return; }
      onFieldFocus?.(locale ? `${highlightId}.${locale}` : `${highlightId}.defaultValue`);
    };

  return {
    fieldIdToHighlightId,
    focusedFieldId,
    focusedLocale,
    handleFieldSectionFocus,
  };
};
